import { useEffect, useState } from "react";
import {
  AlertOctagon,
  CheckCircle2,
  Construction,
  Eye,
  Power,
  ShieldAlert,
} from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  ConfirmDialog,
  DangerConfirmDialog,
  ErrorState,
  SkeletonText,
  StatusPill,
  Switch,
  Textarea,
} from "@/components/ui";
import type { DangerConfirmationCredentials } from "@/components/ui/DangerConfirmDialog";
import { useToast } from "@/components/ui/Toast";
import { useCurrentConfig, useUpdateConfig } from "@/lib/queries";
import { toConfigInput } from "@/lib/utils/config";
import { APP_MODE_META } from "@/lib/utils/statusMeta";
import {
  APP_MODES,
  DANGEROUS_APP_MODES,
  configApi,
  type AppMode,
} from "@/lib/api";

const MODE_ICONS: Record<AppMode, typeof Power> = {
  normal: CheckCircle2,
  read_only: Eye,
  maintenance: Construction,
  force_update: ShieldAlert,
  disabled: AlertOctagon,
};

const MODE_CONSEQUENCES: Record<AppMode, string[]> = {
  normal: [],
  read_only: [],
  maintenance: [
    "IESAPP mostrara una pantalla de mantenimiento a todas las personas usuarias.",
    "Las funciones interactivas quedaran inaccesibles mientras dure.",
  ],
  force_update: [
    "Las personas por debajo de la version minima quedaran bloqueadas hasta actualizar.",
    "El acceso se restringe de forma inmediata al publicar el cambio.",
  ],
  disabled: [
    "IESAPP quedara completamente fuera de servicio para todas las personas usuarias.",
    "Ningun contenido sera accesible hasta reactivar la aplicacion.",
  ],
};

export function AppStatusPage() {
  const configQuery = useCurrentConfig();
  const updateConfig = useUpdateConfig();
  const { toast } = useToast();

  const [pendingMode, setPendingMode] = useState<AppMode | null>(null);
  const [pendingAvailability, setPendingAvailability] = useState<boolean | null>(null);
  const [messageDraft, setMessageDraft] = useState("");
  const [savingMessage, setSavingMessage] = useState(false);

  const config = configQuery.data;

  useEffect(() => {
    if (config) setMessageDraft(config.maintenanceMessage ?? "");
  }, [config]);

  if (configQuery.isLoading) {
    return (
      <div>
        <PageHeader title="Estado de la app" />
        <Card>
          <CardBody>
            <SkeletonText lines={5} />
          </CardBody>
        </Card>
      </div>
    );
  }

  if (configQuery.isError || !config) {
    return (
      <div>
        <PageHeader title="Estado de la app" />
        <Card>
          <CardBody>
            <ErrorState
              description="No se pudo cargar la configuracion actual."
              onRetry={() => configQuery.refetch()}
            />
          </CardBody>
        </Card>
      </div>
    );
  }

  async function confirmationTokenFor(
    targetMode: "maintenance" | "force_update" | "disabled",
    credentials: DangerConfirmationCredentials | undefined,
  ) {
    if (!credentials) throw new Error("Missing emergency confirmation credentials");
    const result = await configApi.createEmergencyConfirmation({
      password: credentials.password,
      totpCode: credentials.totpCode,
      targetMode,
    });
    return result.confirmationToken;
  }

  async function applyModeChange(mode: AppMode, credentials?: DangerConfirmationCredentials) {
    const confirmationToken = DANGEROUS_APP_MODES.includes(mode)
      ? await confirmationTokenFor(
          mode as "maintenance" | "force_update" | "disabled",
          credentials,
        )
      : undefined;
    await updateConfig.mutateAsync({
      ...toConfigInput(config!),
      appMode: mode,
      changeReason: `Cambio de modo a ${mode}`,
      confirmationToken,
    });
    toast({ title: `Modo actualizado a "${APP_MODE_META[mode].label}"`, variant: "success" });
  }

  async function applyAvailabilityChange(
    nextEnabled: boolean,
    credentials?: DangerConfirmationCredentials,
  ) {
    const confirmationToken = !nextEnabled
      ? await confirmationTokenFor("disabled", credentials)
      : undefined;
    await updateConfig.mutateAsync({
      ...toConfigInput(config!),
      appEnabled: nextEnabled,
      changeReason: nextEnabled ? "Reactivar aplicacion" : "Deshabilitar aplicacion",
      confirmationToken,
    });
    toast({
      title: nextEnabled ? "Aplicacion reactivada" : "Aplicacion deshabilitada",
      variant: nextEnabled ? "success" : "info",
    });
  }

  async function handleSaveMessage() {
    setSavingMessage(true);
    try {
      await updateConfig.mutateAsync({
        ...toConfigInput(config!),
        maintenanceMessage: messageDraft.trim() || null,
        changeReason: "Actualizar mensaje de mantenimiento",
      });
      toast({ title: "Mensaje guardado", variant: "success" });
    } catch {
      toast({ title: "No se pudo guardar el mensaje", variant: "error" });
    } finally {
      setSavingMessage(false);
    }
  }

  return (
    <div>
      <PageHeader
        title="Estado de la app"
        description="Controla el modo global de IESAPP. Los modos de riesgo exigen confirmacion reforzada."
      />

      <div className="space-y-4">
        <Card>
          <CardHeader
            title="Modo actual"
            action={<StatusPill tone={APP_MODE_META[config.appMode].tone}>{APP_MODE_META[config.appMode].label}</StatusPill>}
          />
          <CardBody>
            <p className="text-sm text-ink-muted">{APP_MODE_META[config.appMode].description}</p>
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Cambiar modo" description="Selecciona el nuevo estado operativo de IESAPP." />
          <CardBody>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {APP_MODES.map((mode) => {
                const Icon = MODE_ICONS[mode];
                const meta = APP_MODE_META[mode];
                const active = mode === config.appMode;
                const dangerous = DANGEROUS_APP_MODES.includes(mode);
                return (
                  <button
                    key={mode}
                    type="button"
                    disabled={active}
                    onClick={() => setPendingMode(mode)}
                    className={`flex flex-col gap-2 rounded-xl border p-4 text-left transition-colors disabled:cursor-default ${
                      active
                        ? "border-brand-600 bg-brand-50"
                        : "border-ink/10 hover:border-brand-300 hover:bg-brand-50/40"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <Icon
                        className={`h-5 w-5 ${dangerous ? "text-danger-600" : "text-brand-700"}`}
                        aria-hidden="true"
                      />
                      {active && <StatusPill tone="brand">Activo</StatusPill>}
                    </div>
                    <p className="text-sm font-semibold text-ink">{meta.label}</p>
                    <p className="text-xs text-ink-muted">{meta.description}</p>
                  </button>
                );
              })}
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Disponibilidad general" description="Interruptor maestro independiente del modo." />
          <CardBody>
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-ink">Aplicacion disponible</p>
                <p className="text-sm text-ink-muted">
                  Si se desactiva, IESAPP se bloquea aunque el modo sea &ldquo;normal&rdquo;.
                </p>
              </div>
              <Switch
                checked={config.appEnabled}
                onCheckedChange={(next) => setPendingAvailability(next)}
                aria-label="Aplicacion disponible"
              />
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Mensaje de mantenimiento" description="Se muestra cuando el modo es mantenimiento." />
          <CardBody className="space-y-3">
            <Textarea
              value={messageDraft}
              onChange={(e) => setMessageDraft(e.target.value)}
              placeholder="Estamos realizando tareas de mantenimiento. Volveremos pronto."
              maxLength={500}
              aria-label="Mensaje de mantenimiento"
            />
            <div className="flex justify-end">
              <Button
                variant="outline"
                size="sm"
                loading={savingMessage}
                onClick={handleSaveMessage}
                disabled={(config.maintenanceMessage ?? "") === messageDraft}
              >
                Guardar mensaje
              </Button>
            </div>
          </CardBody>
        </Card>
      </div>

      {pendingMode &&
        (DANGEROUS_APP_MODES.includes(pendingMode) ? (
          <DangerConfirmDialog
            open
            onClose={() => setPendingMode(null)}
            onConfirm={(credentials) => applyModeChange(pendingMode, credentials)}
            title={`Cambiar a "${APP_MODE_META[pendingMode].label}"`}
            description="Esta accion se aplica de inmediato a todas las personas usuarias de IESAPP."
            consequences={MODE_CONSEQUENCES[pendingMode]}
            confirmationPhrase={pendingMode}
            confirmLabel="Aplicar modo"
            credentialsRequired
          />
        ) : (
          <ConfirmDialog
            open
            onClose={() => setPendingMode(null)}
            onConfirm={() => applyModeChange(pendingMode)}
            title={`Cambiar a "${APP_MODE_META[pendingMode].label}"`}
            description="Esta accion se aplica de inmediato y queda registrada en auditoria."
            confirmLabel="Aplicar modo"
          />
        ))}

      {pendingAvailability !== null &&
        (pendingAvailability === false ? (
          <DangerConfirmDialog
            open
            onClose={() => setPendingAvailability(null)}
            onConfirm={(credentials) => applyAvailabilityChange(false, credentials)}
            title="Deshabilitar IESAPP"
            description="Esto bloquea el acceso a toda la aplicacion de inmediato."
            consequences={[
              "Todas las personas usuarias perderan acceso a IESAPP de inmediato.",
              "El modo actual pasa a ser irrelevante mientras la disponibilidad este apagada.",
            ]}
            confirmationPhrase="deshabilitar"
            confirmLabel="Deshabilitar"
            credentialsRequired
          />
        ) : (
          <ConfirmDialog
            open
            onClose={() => setPendingAvailability(null)}
            onConfirm={() => applyAvailabilityChange(true)}
            title="Reactivar IESAPP"
            description="La aplicacion volvera a estar disponible segun el modo activo."
            confirmLabel="Reactivar"
          />
        ))}
    </div>
  );
}
